import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { USER_PHOTOS } from "@/lib/brand";

const DEFAULT_SECTIONS = [
  {
    eyebrow: "The cloth",
    title: "Sun-touched cotton, hung low on a branch.",
    body: "We source unbleached, low-impact cotton from small handloom clusters across India — woven slow, dyed slow, dried slower. Soft on skin, kind to the soil it came from.",
    image: USER_PHOTOS.dyed_fabric,
  },
  {
    eyebrow: "The dyes",
    title: "Colour from roots, flowers and leaves.",
    body: "Every shade you see comes from food-safe botanical dyes — turmeric for warm gold, madder for the earthy reds, indigo for the calm blues. No chemical run-off, ever — only what grows back.",
    image: USER_PHOTOS.natural_dye_hands,
  },
  {
    eyebrow: "The pack",
    title: "For the ones who get to wear it.",
    body: "At the end of every stitch is a tail. We make small batches because we want each piece to find its dog or cat — not a warehouse. This is who we make it for.",
    image: USER_PHOTOS.dog_in_field,
  },
];

export default function HowItsMade() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/site-settings").then((r) => setSettings(r.data)); }, []);
  const sections = settings?.how_its_made_sections?.length ? settings.how_its_made_sections : DEFAULT_SECTIONS;

  return (
    <div data-testid="how-its-made-page" className="bg-brand-cream">
      <section className="max-w-5xl mx-auto px-6 sm:px-12 pt-20 sm:pt-32 pb-20">
        <div className="eyebrow mb-4">How it&apos;s made</div>
        <h1 className="font-serif text-5xl sm:text-7xl text-brand-soft leading-[1.02] max-w-3xl">A quiet conversation between <span className="italic text-brand-maroon">cloth, dye, and care.</span></h1>
        <div className="hairline mt-12 max-w-md" />
        <p className="mt-8 text-base sm:text-lg text-brand-soft/75 max-w-2xl leading-relaxed">
          Furshala began over a coffee, on a rainy Tuesday, with a question — what would a coat feel like
          if no one in the room rushed it? This is what came out.
        </p>
      </section>

      <div className="max-w-6xl mx-auto px-6 sm:px-12 space-y-28 sm:space-y-40 pb-40">
        {sections.map((s, i) => (
          <div key={i} className={`grid lg:grid-cols-12 gap-10 lg:gap-20 items-center ${i % 2 ? "lg:[&>div:first-child]:order-2" : ""}`}>
            <div className="lg:col-span-5">
              <div className="eyebrow mb-4">{s.eyebrow}</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-brand-soft leading-[1.05]">{s.title}</h2>
              <div className="hairline my-7 max-w-[100px]" />
              <p className="text-brand-soft/75 leading-relaxed text-base sm:text-lg">{s.body}</p>
            </div>
            <div className="lg:col-span-7 aspect-[4/5] sm:aspect-[4/3] rounded-sm overflow-hidden bg-brand-ivory shadow-[0_30px_70px_-25px_rgba(31,24,21,0.25)]">
              {s.image && <img src={fileUrl(s.image)} alt={s.title} className="w-full h-full object-cover" />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
